import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import {
    type User as FirebaseUser,
    signInWithEmailAndPassword,
    signOut as firebaseSignOut,
    onAuthStateChanged,
} from 'firebase/auth';
import { collection, doc, getDoc, getDocFromServer, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../../lib/firebase';
import type { RequestedPlan } from '@/lib/plans';
import type { User, UserRole, SystemModule, PermissionAction, ModulePermissions, Role } from '../../types';
import { SYSTEM_MODULES } from '../../types';
import { userHasPermission } from '@/lib/permissions';
import { writeAuditLog } from '@/lib/auditLog';
import { primeRoleNameMap } from '@/lib/roleNameMap';
import { isKidMember } from '@/utils/roleChecks';

interface AuthContextType {
    currentUser: User | null;
    loading: boolean;
    signUp: (email: string, password: string, displayName: string, branchId?: string, mobile?: string, isKid?: boolean, parentData?: { name?: string; email?: string; mobile?: string; id?: string }, initialRole?: UserRole, whatsappOptIn?: boolean, referralCode?: string, requestedPlan?: RequestedPlan) => Promise<string | void>;
    signIn: (email: string, password: string) => Promise<void>;
    signOut: () => Promise<void>;
    hasPermission: (module: SystemModule, action: PermissionAction) => boolean;
    // Impersonation
    isImpersonating: boolean;
    impersonatedUser: User | null;
    impersonateUser: (user: User) => Promise<void>;
    exitImpersonation: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

// Helper to check if two user objects are deeply equal (to prevent infinite loops)
const areUsersEqual = (user1: User | null, user2: User | null): boolean => {
    if (user1 === user2) return true;
    if (!user1 || !user2) return false;

    // Compare all primitive fields
    return (
        user1.uid === user2.uid &&
        user1.email === user2.email &&
        user1.displayName === user2.displayName &&
        user1.photoURL === user2.photoURL &&
        user1.role === user2.role &&
        user1.status === user2.status &&
        user1.totalScore === user2.totalScore &&
        user1.branchId === user2.branchId &&
        user1.subscriptionStatus === user2.subscriptionStatus &&
        user1.activeSubscriptionId === user2.activeSubscriptionId &&
        user1.roleId === user2.roleId &&
        // Compare arrays
        JSON.stringify(user1.roleIds) === JSON.stringify(user2.roleIds) &&
        // Permissions can be compared by stringifying since it's a simple object structure
        JSON.stringify(user1.permissions) === JSON.stringify(user2.permissions) &&
        // Tours status must be tracked for onboarding to stop correctly
        JSON.stringify(user1.tours) === JSON.stringify(user2.tours)
    );
};


export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [currentUser, setCurrentUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);

    // Use ref to track previous user value to prevent infinite loops
    const previousUserRef = useRef<User | null>(null);

    // Cache for the `roles` collection (Task #16).
    //
    // Pre-Task-#16 the user-doc snapshot handler re-fetched `roles` on
    // every user-doc change. That ran on every score increment, every
    // attendance write, every tours update — wasteful for a collection
    // that effectively only changes when an admin edits the RBAC matrix.
    //
    // Now: ONE `onSnapshot` listener on `roles` is set up at mount, the
    // result is cached in `rolesRef`, and consumers (the user-doc
    // handler, `impersonateUser`) read from the ref. Role edits still
    // propagate live because the snapshot listener fires on every
    // remote change. The user-doc handler awaits `rolesReadyRef.current`
    // on first run to guarantee the cache is populated before the
    // initial permission calculation.
    const rolesRef = useRef<Role[]>([]);
    const rolesReadyRef = useRef<Promise<void> | null>(null);
    const rolesResolverRef = useRef<(() => void) | null>(null);

    // Impersonation State
    const [impersonatedUser, setImpersonatedUser] = useState<User | null>(null);

    const isImpersonating = !!impersonatedUser;

    // Impersonate a user (Super Admin only)
    const impersonateUser = async (user: User) => {
        if (!currentUser || currentUser.role !== 'super_admin') {
            console.error('Only super admins can impersonate users');
            return;
        }

        // Resolve roleNames from roleIds if not already present.
        // Uses the cached `rolesRef` (Task #16) instead of a fresh
        // `getDocs` so impersonation doesn't re-fetch the entire roles
        // collection. Awaits the initial-load promise to handle the
        // edge case where impersonation is triggered before the first
        // roles snapshot has fired.
        let enhancedUser = { ...user };
        if (user.roleIds && user.roleIds.length > 0 && (!user.roleNames || user.roleNames.length === 0)) {
            try {
                if (rolesReadyRef.current) await rolesReadyRef.current;
                const roleNameMap: Record<string, string> = {};
                rolesRef.current.forEach(r => {
                    roleNameMap[r.id] = r.name?.toLowerCase() || '';
                });

                // Resolve roleIds to roleNames
                const resolvedNames = user.roleIds
                    .map(rId => roleNameMap[rId])
                    .filter(name => !!name);

                enhancedUser = { ...user, roleNames: resolvedNames };
                console.log('[impersonateUser] Resolved roleNames:', resolvedNames);
            } catch (err) {
                console.error('[impersonateUser] Failed to resolve roleNames:', err);
            }
        }

        // Switch to impersonated user with resolved roleNames
        setImpersonatedUser(enhancedUser);
    };

    // Exit impersonation and restore original user
    const exitImpersonation = () => {
        setImpersonatedUser(null);
    };

    // The effective current user (impersonated or real)
    const effectiveUser = impersonatedUser || currentUser;

    // Fetch user profile from Firestore
    const fetchUserProfile = async (firebaseUser: FirebaseUser): Promise<User | null> => {
        try {
            let userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));

            /**
             * Self-heal a poisoned local cache before declaring the profile missing.
             *
             * Incident (2026-06-01): after a sign-out, login failed with
             * "User profile not found for UID: g12bPixOTXUS16BEM5fwDyJdNxz2".
             * The Firestore doc existed and Firebase Auth was healthy — but the
             * Firestore SDK's IndexedDB persistent cache was serving a stale
             * `!exists` result for that doc. `getDoc` is cache-first, so it kept
             * returning "missing" and the only workaround was clearing site data
             * via DevTools.
             *
             * A `!exists` result is therefore not trustworthy on its own: retry
             * once with `getDocFromServer`, which bypasses the local cache. Only a
             * server-confirmed `!exists` means the profile is genuinely absent.
             * The warn below tells us whether cache poisoning is an ongoing problem
             * in the wild or was a one-off.
             */
            if (!userDoc.exists()) {
                console.warn(`[AuthProvider] Cached read returned !exists for UID ${firebaseUser.uid} — retrying from server to rule out a stale local cache.`);
                userDoc = await getDocFromServer(doc(db, 'users', firebaseUser.uid));
            }

            if (userDoc.exists()) {
                const userData = userDoc.data();

                // Helper to safely convert Firestore timestamp or string to Date
                const toDate = (val: unknown): Date => {
                    if (!val) return new Date();
                    if (typeof val === 'object' && typeof (val as { toDate?: unknown }).toDate === 'function') {
                        return (val as { toDate: () => Date }).toDate();
                    }
                    if (val instanceof Date) return val;
                    return new Date(val as string | number);
                };

                return {
                    uid: firebaseUser.uid,
                    email: firebaseUser.email!,
                    displayName: userData.displayName || firebaseUser.displayName,
                    photoURL: userData.photoURL || firebaseUser.photoURL,
                    role: userData.role || 'student',
                    status: userData.status || 'active', // Default to active for legacy users
                    totalScore: userData.totalScore || 0,
                    referralCode: userData.referralCode,
                    branchId: userData.branchId,
                    subscriptionStatus: userData.subscriptionStatus,
                    activeSubscriptionId: userData.activeSubscriptionId,
                    belt: userData.belt, // Belt rank
                    stripes: userData.stripes, // Belt stripes (0-4)
                    // The plan asked for at signup — shown back to the member while they wait.
                    requestedPlanId: userData.requestedPlanId,
                    requestedPlanName: userData.requestedPlanName,
                    requestedPlanPrice: userData.requestedPlanPrice,
                    requestedPlanDurationDays: userData.requestedPlanDurationDays,
                    createdAt: toDate(userData.createdAt),
                    updatedAt: toDate(userData.updatedAt),
                };
            }
            return null;
        } catch (error) {
            console.error('Error fetching user profile:', error);
            // Throw the original error so we know if it's permission/network related.
            // Mirrors the previous `error.message || error` fallback for non-Error throws.
            const detail = error instanceof Error && error.message ? error.message : String(error);
            throw new Error(`Failed to fetch user profile for [${firebaseUser.uid}]: ${detail}`);
        }
    };

    // Sign up new user
    const signUp = async (
        email: string,
        password: string,
        displayName: string,
        branchId?: string,
        mobile?: string,
        // New optional params for Kid Logic
        isKid?: boolean,
        parentData?: { name?: string; email?: string; mobile?: string; id?: string },
        initialRole?: UserRole,
        // Wazaio WhatsApp opt-in flag (Phase 1). Captured from signup form
        // checkbox; defaults to false here so callers that don't pass the
        // arg are safe-by-default (no inadvertent opt-in). The Register UI
        // defaults the checkbox to true.
        whatsappOptIn?: boolean,
        /** A friend's referral code, typed at signup. Stored as `referredByCode`; the server
         *  resolves it and pays the referrer when this member's first subscription starts. */
        referralCode?: string,
        /** The plan this member ASKED for at signup. Recorded on the account so whoever
         *  approves them knows what they want; it creates no subscription and no finance row. */
        requestedPlan?: RequestedPlan,
    ) => {
        // Use a secondary app instance to create user without logging out the current user
        const { initializeApp, deleteApp } = await import('firebase/app');
        const { getAuth, createUserWithEmailAndPassword: createSecondaryUser, signOut: signOutSecondary } = await import('firebase/auth');
        // const { getDocs, query, collection, where } = await import('firebase/firestore'); // Unused

        const { getFirestore, doc: getDocRef, setDoc: setDocSecondary } = await import('firebase/firestore');

        // Create a unique name for the secondary app to avoid conflicts
        const secondaryAppName = `secondaryApp-${Date.now()}`;
        const firebaseConfig = {
            apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
            authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
            projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
            storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
            messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
            appId: import.meta.env.VITE_FIREBASE_APP_ID,
        };

        const secondaryApp = initializeApp(firebaseConfig, secondaryAppName);
        const secondaryAuth = getAuth(secondaryApp);
        // Initialize Firestore for the secondary app to ensure we write as the authenticated *new* user
        const secondaryDb = getFirestore(secondaryApp);

        try {
            // 1. If linked to parent, try to find parent first? 
            // Actually, we do this after creation or efficiently before.
            // let foundParentId: string | undefined = undefined; // Unused 

            if (isKid && parentData?.email) {
                // Try to find existing parent by email
                // Note: This requires query permissions on 'users' collection for unauthenticated users 
                // IF we run this on client. But here we are logged in? 
                // Wait, signUp is usually called when NOT logged in.
                // Firestore rules might block 'list' on users for public.
                // However, we can just proceed and let the Admin reconcile, OR
                // use a Cloud Function. For now, we'll try best effort or just store metadata.

                // Let's just store the metadata to avoid permission errors on public sign-up.
                // The admin can link later, or we can use a server-side trigger.
            }

            const userCredential = await createSecondaryUser(secondaryAuth, email, password);
            const user = userCredential.user;

            // Create user profile in Firestore (using the secondary app's db instance which is Authenticated)
            console.log('Creating user profile with branchId:', branchId);

            const referredByCode = (referralCode || '').trim().toUpperCase().slice(0, 12);
            const userProfile: Record<string, unknown> = {
                email: user.email!,
                displayName,
                // Whose code this member typed. The server resolves it and pays that member
                // when this one's first subscription starts — never at signup.
                ...(referredByCode ? { referredByCode } : {}),
                photoURL: null,
                role: initialRole || (isKid ? 'kid' : 'student'), // Use explicit role or fallback
                status: 'pending', // Default to pending for approval
                createdAt: new Date(),
                updatedAt: new Date(),
            };

            if (branchId) {
                userProfile.branchId = branchId;
            }

            if (mobile) {
                userProfile.mobile = mobile;
            }

            // A request, never a purchase: the admin activates it after payment at the academy.
            if (requestedPlan) {
                userProfile.requestedPlanId = requestedPlan.requestedPlanId;
                userProfile.requestedPlanName = requestedPlan.requestedPlanName;
                userProfile.requestedPlanPrice = requestedPlan.requestedPlanPrice;
                userProfile.requestedPlanDurationDays = requestedPlan.requestedPlanDurationDays;
                userProfile.requestedPlanAt = requestedPlan.requestedPlanAt;
            }
            // Wazaio WhatsApp integration: capture opt-in state at signup.
            // Always write the field (true OR false) so the user has an
            // explicit value rather than undefined — makes the opt-in
            // check in the Cloud Function unambiguous.
            userProfile.whatsappOptIn = whatsappOptIn === true;

            if (isKid) {
                // `isKid` is no longer written — the role above ('kid') is the division.
                if (parentData) {
                    // Store as metadata for now since we can't easily query users publicly safely
                    userProfile.parentMetadata = parentData;
                    if (parentData.id) {
                        userProfile.parentId = parentData.id;
                    }
                }
            }

            // USE SECONDARY DB INSTANCE
            await setDocSecondary(getDocRef(secondaryDb, 'users', user.uid), userProfile);

            // Create Student Profile only for student-like roles (not parents)
            const roleForProfile = initialRole || (isKid ? 'kid' : 'student');
            if (roleForProfile === 'student' || roleForProfile === 'kid' || roleForProfile === 'coach') {
                await setDocSecondary(getDocRef(secondaryDb, 'studentProfiles', user.uid), {
                    userId: user.uid,
                    belt: 'White',
                    createdAt: new Date(),
                    updatedAt: new Date(),
                });
            }

            // Welcome bonus (100 XP for `account_created` challenge) is now
            // awarded server-side by the onUserCreated Cloud Function trigger
            // (Task #22.5). The client used to write here as the newly-created
            // user via a secondary Firebase app instance, but Task #22 locked
            // pointTransactions creates and Task #6 locked userChallenges
            // creates — leaving only the trigger path viable. The trigger
            // dedups via userChallenges so it's idempotent.

            // Sign out from secondary app to be clean
            await signOutSecondary(secondaryAuth);

            return user.uid;
        } catch (error) {
            console.error('Error creating user with secondary app:', error);
            throw error;
        } finally {
            // Clean up the secondary app
            await deleteApp(secondaryApp);
        }
    };

    // Sign in existing user
    const signIn = async (email: string, password: string) => {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);

        // Verify user profile exists in Firestore
        const userProfile = await fetchUserProfile(userCredential.user);

        if (!userProfile) {
            // If no profile exists, sign out immediately and Log the failure for debugging
            const uid = userCredential.user.uid;
            const email = userCredential.user.email;

            try {
                // Canonical audit shape via the Task #21 helper. The
                // firestore.rules block client-side creates (Task #6),
                // so this write is rule-denied today and gets swallowed
                // by the catch — but the SHAPE is now correct, so when
                // this path eventually moves to a callable the doc it
                // emits will already match `AuditLogEntry`.
                await writeAuditLog(db, {
                    action: 'login.failure',
                    module: 'auth',
                    actorId: uid,
                    targetId: uid,
                    targetName: email ?? undefined,
                    details: JSON.stringify({
                        reason: 'MISSING_PROFILE_DOCUMENT',
                        uid,
                        email,
                    }),
                    status: 'failure',
                });
                console.log('Logged missing profile failure to audit_logs');
            } catch (err) {
                // audit_logs creates are denied at the rules layer (Task #6).
                // Swallow — canonical path is a future callable migration.
                console.warn('audit_logs write blocked by rules — needs server-side callable', err);
            }

            await firebaseSignOut(auth);
            throw new Error(`User profile not found for UID: ${uid}. Please ensure a document with this EXACT ID exists in the 'users' collection.`);
        }
    };

    // Sign out
    const signOut = async () => {
        await firebaseSignOut(auth);
    };

    // Check if user has permission
    /**
     * Matrix-aware capability check for the EFFECTIVE (possibly impersonated) user.
     * Delegates to the shared `userHasPermission` helper so React gates, route
     * resolvers and non-React code can never drift apart — the admin bypass and the
     * coordinator baseline used to be inlined here and nowhere else, which is how
     * /attendance ended up disagreeing with the matrix.
     */
    const hasPermission = (module: SystemModule, action: PermissionAction): boolean =>
        userHasPermission(effectiveUser, module, action);

    // Roles cache snapshot listener (Task #16).
    //
    // Set up ONCE on mount. Fires on every roles-collection write
    // (admin edits the RBAC matrix); on first fire it resolves the
    // `rolesReadyRef` promise so the user-doc handler and impersonation
    // know the cache is hot.
    useEffect(() => {
        // Allocate the ready-promise BEFORE the listener attaches so
        // any consumer awaiting it (the user-doc handler is async and
        // can race) sees a pending promise rather than null.
        rolesReadyRef.current = new Promise<void>((resolve) => {
            rolesResolverRef.current = resolve;
        });

        const toDate = (val: unknown): Date => {
            if (!val) return new Date();
            if (typeof val === 'object' && val !== null && 'toDate' in val && typeof (val as { toDate: unknown }).toDate === 'function') {
                return (val as { toDate: () => Date }).toDate();
            }
            if (val instanceof Date) return val;
            return new Date(val as string | number);
        };

        const unsub = onSnapshot(
            collection(db, 'roles'),
            (snap) => {
                const next: Role[] = [];
                snap.forEach((d) => {
                    const rData = d.data();
                    next.push({
                        id: d.id,
                        name: rData.name,
                        permissions: rData.permissions,
                        createdAt: toDate(rData.createdAt),
                        updatedAt: toDate(rData.updatedAt),
                    });
                });
                rolesRef.current = next;
                // Hand the same live list to the role-name map, so roleChecks can
                // resolve a user's roleIds (doc ids) to names without a second read.
                // Without this, hasRoleNamed() falls back to the legacy `role` field
                // for every user except the signed-in one.
                primeRoleNameMap(next);
                // First snapshot landed — unblock any consumers waiting
                // on the initial-load promise.
                if (rolesResolverRef.current) {
                    rolesResolverRef.current();
                    rolesResolverRef.current = null;
                }
            },
            (err) => {
                console.error('[AuthProvider] roles snapshot error:', err);
                // Still resolve so we don't hang user-doc permission
                // calculation forever on a transient permission/network
                // error. Cache stays empty; calculation falls back to
                // zero permissions which is the safe default.
                if (rolesResolverRef.current) {
                    rolesResolverRef.current();
                    rolesResolverRef.current = null;
                }
            }
        );

        return () => unsub();
    }, []);

    // Listen for auth state changes.
    useEffect(() => {
        let unsubscribeSnapshot: (() => void) | null = null;

        const unsubscribeAuth = onAuthStateChanged(auth, async (firebaseUser) => {
            if (firebaseUser) {
                // Set up real-time listener for the user profile
                unsubscribeSnapshot = onSnapshot(doc(db, 'users', firebaseUser.uid), async (docConfig) => {
                    if (docConfig.exists()) {
                        const userData = docConfig.data();

                        // Helper to safely convert Firestore timestamp or string to Date
                        const toDate = (val: unknown): Date => {
                            if (!val) return new Date();
                            if (typeof val === 'object' && typeof (val as { toDate?: unknown }).toDate === 'function') {
                                return (val as { toDate: () => Date }).toDate();
                            }
                            if (val instanceof Date) return val;
                            return new Date(val as string | number);
                        };

                        // --- RBAC CALCULATION ---
                        // 1. Read roles from the cache (Task #16).
                        // Pre-#16 this re-ran `getDocs(collection(db, 'roles'))`
                        // on every user-doc snapshot. Now: a separate snapshot
                        // listener (see the rolesRef useEffect below) keeps
                        // `rolesRef.current` populated; we just await the
                        // initial-load promise to handle cold-start ordering.
                        let effectivePermissions: Record<SystemModule, ModulePermissions> | undefined = undefined;
                        let resolvedRoleNames: string[] = [];

                        try {
                            if (rolesReadyRef.current) await rolesReadyRef.current;
                            const allSystemRoles: Role[] = rolesRef.current;

                            // 2. Identify User's Roles
                            const userRoleIds: string[] = userData.roleIds || (userData.roleId ? [userData.roleId] : []);

                            // fallback for legacy 'role' field string matching if no IDs
                            if (userRoleIds.length === 0 && userData.role) {
                                // Try to find a role with that name
                                const matching = allSystemRoles.find(r => r.name.toLowerCase() === (userData.role as string).toLowerCase());
                                if (matching) userRoleIds.push(matching.id);
                            }

                            // 3. Merge Permissions
                            // Initialize with empty/false
                            const merged: Record<string, ModulePermissions> = {};
                            const modules = SYSTEM_MODULES; // from types

                            // Helper: Create empty permit
                            const emptyPerm = (): ModulePermissions => ({ create: false, read: false, update: false, delete: false });

                            // Populate defaults
                            modules.forEach(m => merged[m] = emptyPerm());

                            // Iterate user's roles
                            userRoleIds.forEach(rId => {
                                const roleDoc = allSystemRoles.find(r => r.id === rId);
                                if (roleDoc && roleDoc.permissions) {
                                    modules.forEach(mod => {
                                        const rPerms = roleDoc.permissions[mod];
                                        if (rPerms) {
                                            // Union (OR) logic
                                            merged[mod].create = merged[mod].create || rPerms.create;
                                            merged[mod].read = merged[mod].read || rPerms.read;
                                            merged[mod].update = merged[mod].update || rPerms.update;
                                            merged[mod].delete = merged[mod].delete || rPerms.delete;
                                        }
                                    });
                                }
                            });

                            effectivePermissions = merged as Record<SystemModule, ModulePermissions>;

                            // Resolve role names from role IDs for use in helper functions
                            // This allows isCoordinator, isCoach, etc. to check by role name
                            resolvedRoleNames = userRoleIds
                                .map(rId => allSystemRoles.find(r => r.id === rId)?.name?.toLowerCase())
                                .filter((name): name is string => !!name);

                        } catch (err) {
                            console.error("Error calculating permissions:", err);
                        }

                        // SECURITY PATCH: Force strict permissions for Parent role
                        // This ensures that even if the 'Parent' role in DB has incorrect Admin permissions,
                        // real parent users will NOT see Admin features.
                        if (userData.role === 'parent' || isKidMember(userData as User)) {
                            const emptyPerm = (): ModulePermissions => ({ create: false, read: false, update: false, delete: false });

                            // Start with basic restricted set
                            const restrictedPerms: Record<string, ModulePermissions> = {};
                            const modules = SYSTEM_MODULES;
                            modules.forEach(m => restrictedPerms[m] = emptyPerm());

                            // Grant specific necessary access
                            // Parents need read access to these for their dashboard to work (if protected by guards)
                            // But mostly, Sidebar items check for these.

                            // explicitly deny Admin modules and set granular permissions
                            if (effectivePermissions) {
                                // Block Users
                                effectivePermissions.users_view = emptyPerm();
                                effectivePermissions.users_manage_students = emptyPerm();
                                effectivePermissions.users_manage_coaches = emptyPerm();
                                effectivePermissions.users_manage_staff = emptyPerm();
                                effectivePermissions.users_manage_parents = emptyPerm();

                                // Block Finance
                                effectivePermissions.finance_view = emptyPerm();
                                effectivePermissions.finance_income_view = emptyPerm();
                                effectivePermissions.finance_income_manage = emptyPerm();
                                effectivePermissions.finance_expenses_view = emptyPerm();
                                effectivePermissions.finance_expenses_manage = emptyPerm();
                                effectivePermissions.finance_payouts_view = emptyPerm();
                                effectivePermissions.finance_payouts_manage = emptyPerm();
                                effectivePermissions.finance_settings = emptyPerm();

                                // Block Subscriptions
                                effectivePermissions.subscriptions_view = emptyPerm();
                                effectivePermissions.subscriptions_manage = emptyPerm();
                                effectivePermissions.subscriptions_plans = emptyPerm();

                                // Allow Schedule View
                                effectivePermissions.schedule_view = { ...emptyPerm(), read: true };
                                effectivePermissions.schedule_manage = emptyPerm();

                                // Allow LMS View
                                effectivePermissions.lms_view = { ...emptyPerm(), read: true };
                                effectivePermissions.lms_manage = emptyPerm();

                                // Block Operations & Settings
                                effectivePermissions.branches_view = emptyPerm();
                                effectivePermissions.branches_manage = emptyPerm();
                                effectivePermissions.roles_view = emptyPerm();
                                effectivePermissions.roles_manage = emptyPerm();
                                effectivePermissions.settings_general = emptyPerm();
                                // Note: settings_profile (Access Profile) is left as-is (likely allowed)

                                // Block Feed & Messages
                                effectivePermissions.feed_view = emptyPerm();
                                effectivePermissions.feed_post = emptyPerm();
                                effectivePermissions.feed_moderate = emptyPerm();
                                effectivePermissions.messages_view = emptyPerm();
                                effectivePermissions.messages_send = emptyPerm();
                            }
                        }

                        const userProfile: User = {
                            uid: firebaseUser.uid,
                            email: firebaseUser.email!,
                            displayName: userData.displayName || firebaseUser.displayName,
                            photoURL: userData.photoURL || firebaseUser.photoURL,
                            role: userData.role || 'student',
                            status: userData.status || 'active', // Default to active
                            roleId: userData.roleId,     // Pass through
                            roleIds: userData.roleIds,   // Pass through (document IDs)
                            roleNames: resolvedRoleNames, // NEW: Resolved role names for helper checks
                            permissions: effectivePermissions, // <--- ATTACHED
                            totalScore: userData.totalScore || 0,
                            branchId: userData.branchId,
                            subscriptionStatus: userData.subscriptionStatus,
                            activeSubscriptionId: userData.activeSubscriptionId,
                            belt: userData.belt, // Belt rank
                            stripes: userData.stripes, // Belt stripes (0-4)
                            createdAt: toDate(userData.createdAt),
                            updatedAt: toDate(userData.updatedAt),
                            // Family + multi-role pass-through. This builder is
                            // hand-assembled (not a spread), so every field the
                            // User type gains must be added here explicitly or it
                            // is silently dropped from `currentUser`. Mirrors the
                            // denormalization in useUsers.ts — see the commentary
                            // there for why each field matters.
                            //
                            // parentId + isKidProfile are load-bearing TODAY:
                            // OnboardingWizard.tsx:53 branches on
                            // `!currentUser.isKidProfile && !currentUser.parentId`.
                            // While dropped it evaluated as if every caller were a
                            // self-signup adult. (`isKid` is retired — division is
                            // isKidMember, which reads `role`.)
                            parentId: userData.parentId,
                            referralCode: userData.referralCode,
                            isKidProfile: userData.isKidProfile,
                            age: userData.age,
                            childrenIds: userData.childrenIds,
                            isStudent: userData.isStudent,
                            isParent: userData.isParent,
                            isCoach: userData.isCoach,
                            isAdmin: userData.isAdmin,
                        };

                        // CRITICAL FIX: Only update if user data actually changed
                        // This prevents infinite loop from object recreation
                        if (!areUsersEqual(previousUserRef.current, userProfile)) {
                            previousUserRef.current = userProfile;
                            setCurrentUser(userProfile);
                        }
                    } else {
                        // Profile missing
                    }
                    setLoading(false);
                }, (error) => {
                    console.error("Error listening to user profile:", error);
                    setLoading(false);
                });

            } else {
                if (unsubscribeSnapshot) {
                    unsubscribeSnapshot();
                    unsubscribeSnapshot = null;
                }
                setCurrentUser(null);
                setLoading(false);
            }
        });

        return () => {
            unsubscribeAuth();
            if (unsubscribeSnapshot) {
                unsubscribeSnapshot();
            }
        };
    }, []);

    const value: AuthContextType = {
        currentUser: effectiveUser, // Return effective user (impersonated if active)
        loading,
        signUp,
        signIn,
        signOut,
        hasPermission,
        // Impersonation
        isImpersonating,
        impersonatedUser,
        impersonateUser,
        exitImpersonation,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
